import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, StickyNote } from "lucide-react";
import { api, errMsg, eur, fmtDate, KIND_LABELS, TIME_BANDS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";

const OPEN = ["richiesta_inviata", "in_attesa_pagamento", "confermato"];

async function run(fn, ok, reload) {
  try {
    await fn();
    toast.success(ok);
    reload();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function MoveForm({ a, reload, onDone }) {
  const [date, setDate] = useState(a.date);
  const [time, setTime] = useState(a.kind === "domicilio" ? a.time_band : a.time);
  const [slots, setSlots] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => {
    if (a.kind === "domicilio" || !date) return;
    api.get(`/professionals/${a.professional_id}/slots`, { params: { date } }).then(({ data }) => setSlots(data.filter((s) => s.available)));
  }, [a, date]);
  const opts = a.kind === "domicilio" ? Object.entries(TIME_BANDS) : slots.map((s) => [s.time, s.time]);
  const save = () => run(() => api.post(`/staff/appointments/${a.id}/reschedule`, { date, note, ...(a.kind === "domicilio" ? { time_band: time } : { time }) }), "Appuntamento spostato", () => { onDone(); reload(); });
  return (
    <div className="mt-3 grid gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-[auto_auto_1fr_auto]" data-testid={`op-move-form-${a.id}`}>
      <Input type="date" data-testid={`op-move-date-${a.id}`} value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="h-9" />
      <select data-testid={`op-move-time-${a.id}`} value={time || ""} onChange={(e) => setTime(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
        <option value="">{a.kind === "domicilio" ? "Fascia" : "Orario"}</option>
        {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Input data-testid={`op-move-note-${a.id}`} placeholder="Motivo per il paziente (facoltativo)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9" />
      <Button size="sm" disabled={!date || !time} data-testid={`op-move-save-${a.id}`} onClick={save} className="h-9 bg-emerald-600 hover:bg-emerald-700">Salva</Button>
    </div>
  );
}

function NoteForm({ a, reload, onDone }) {
  const [note, setNote] = useState("");
  const save = () => run(() => api.post(`/staff/appointments/${a.id}/note`, { note }), "Nota inviata al paziente", () => { onDone(); reload(); });
  return (
    <div className="mt-3 flex gap-2" data-testid={`op-note-form-${a.id}`}>
      <Input data-testid={`op-note-input-${a.id}`} placeholder="Nota per il paziente" value={note} onChange={(e) => setNote(e.target.value)} className="h-9" />
      <Button size="sm" disabled={!note.trim()} data-testid={`op-note-save-${a.id}`} onClick={save} className="h-9 bg-emerald-600 hover:bg-emerald-700">Invia</Button>
    </div>
  );
}

function ApptRow({ a, reload }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState("");
  const toggle = (k) => setOpen(open === k ? "" : k);
  const post = (action, ok) => run(() => api.post(`/staff/appointments/${a.id}/${action}`), ok, reload);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`staff-appt-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{a.patient_name} · {a.service_name}</p>
          <p className="text-xs text-slate-500">{KIND_LABELS[a.kind]} · {a.professional_name} · {fmtDate(a.date)} {a.time || TIME_BANDS[a.time_band]} {a.city ? `· ${a.city}, ${a.address}` : ""} · {eur(a.amount)}</p>
          {a.notes && <p className="mt-1 text-xs text-slate-600">Note paziente: {a.notes}</p>}
          {a.staff_note && <p className="mt-1 text-xs text-emerald-700" data-testid={`op-staff-note-${a.id}`}>Nota operatore: {a.staff_note}</p>}
        </div>
        <StatusBadge status={a.status} />
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {a.status === "richiesta_inviata" && <>
          <Button size="sm" data-testid={`staff-accept-${a.id}`} onClick={() => post("accetta", "Richiesta accettata")} className="bg-sky-600 hover:bg-sky-700">Accetta</Button>
          <Button size="sm" variant="outline" data-testid={`staff-reject-${a.id}`} onClick={() => post("rifiuta", "Richiesta rifiutata")}>Rifiuta</Button>
        </>}
        {OPEN.includes(a.status) && <Button size="sm" variant="outline" data-testid={`op-move-btn-${a.id}`} onClick={() => toggle("move")}><CalendarClock className="mr-1 h-3.5 w-3.5" />Sposta</Button>}
        <Button size="sm" variant="outline" data-testid={`op-note-btn-${a.id}`} onClick={() => toggle("note")}><StickyNote className="mr-1 h-3.5 w-3.5" />Nota</Button>
        {a.status === "confermato" && <Button size="sm" variant="outline" data-testid={`staff-complete-${a.id}`} onClick={() => post("completa", "Completato")}>Segna completato</Button>}
        {a.status === "confermato" && a.kind === "video" && <Button size="sm" data-testid={`staff-join-${a.id}`} onClick={() => navigate(`/video/${a.id}`)} className="bg-cyan-600 hover:bg-cyan-700">Avvia video consulto</Button>}
      </div>
      {open === "move" && <MoveForm a={a} reload={reload} onDone={() => setOpen("")} />}
      {open === "note" && <NoteForm a={a} reload={reload} onDone={() => setOpen("")} />}
    </div>
  );
}

export function OperatorAppointments({ kind }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/staff/appointments", { params: kind ? { kind } : {} }).then(({ data }) => setItems(data)).catch((e) => { toast.error(errMsg(e)); setItems([]); }), [kind]);
  useEffect(() => {
    load();
  }, [load]);
  if (items === null) return null;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="staff-agenda-empty">Nessuna prenotazione da gestire in questo reparto.</p>;
  const pending = items.filter((a) => a.status === "richiesta_inviata").length;
  const sorted = [...items].sort((x, y) => (x.status === "richiesta_inviata") === (y.status === "richiesta_inviata") ? x.date.localeCompare(y.date) : x.status === "richiesta_inviata" ? -1 : 1);
  return (
    <div className="space-y-2" data-testid="staff-agenda">
      {pending > 0 && <p className="text-xs font-semibold text-amber-700" data-testid="staff-agenda-pending">{pending} richieste in attesa di risposta</p>}
      {sorted.map((a) => <ApptRow key={a.id} a={a} reload={load} />)}
    </div>
  );
}
